/**
 * `<WidgetSettingsModal>` — configure a dashboard widget, for BOTH adding a new
 * one and editing an existing one. Widget types on the left (same card style as
 * the Add-column modal); the right side shows the minimal config (source
 * column, aggregation, bucket) plus a LIVE PREVIEW from the board's real rows.
 *
 * When `initial` is set (edit mode) the form is seeded from that widget and the
 * result keeps its id + grid geometry; otherwise a fresh widget is created.
 */
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import {
  BarChart3,
  BatteryMedium,
  Hash,
  LineChart,
  PieChart,
  Type as TypeIcon,
} from 'lucide-react';

import type {
  DashboardAggregation,
  DashboardDateBucket,
  DashboardWidget,
  DashboardWidgetType,
} from '@/entities/tracker';
import { i18n } from '@/shared/i18n';
import { FormDialog } from '@/shared/ui/FormDialog';

import type { DashboardItem } from '../../lib/dashboard';
import type { CustomColumn } from '../../lib/useTrackersBoard';
import { DashboardWidgetBody } from './DashboardWidgets';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';

/* eslint-disable i18next/no-literal-string -- column type wire values and select sentinel ids */
const CATEGORICAL_TYPES = new Set(['status', 'people', 'brief_type', 'checkbox']);
const DATE_TYPES = new Set(['date', 'date_range']);

/** Sentinel for "no source column" select values (Radix forbids empty ids). */
const ALL_ITEMS = '__all__';
const CREATED_AT = '__created__';
/* eslint-enable i18next/no-literal-string */

interface WidgetTypeOption {
  type: DashboardWidgetType;
  labelKey: ParseKeys<'trackers'>;
  descriptionKey: ParseKeys<'trackers'>;
  Icon: typeof TypeIcon;
}

const WIDGET_TYPES: WidgetTypeOption[] = [
  {
    type: 'kpi',
    labelKey: 'trackersBoard.widgetSettingsModal.number',
    descriptionKey: 'trackersBoard.widgetSettingsModal.oneBigNumberACount',
    Icon: Hash,
  },
  {
    type: 'donut',
    labelKey: 'trackersBoard.widgetSettingsModal.donut',
    descriptionKey: 'trackersBoard.widgetSettingsModal.shareOfItemsAcrossA',
    Icon: PieChart,
  },
  {
    type: 'bar',
    labelKey: 'trackersBoard.widgetSettingsModal.barChart',
    descriptionKey: 'trackersBoard.widgetSettingsModal.compareCountsPerValue',
    Icon: BarChart3,
  },
  {
    type: 'line',
    labelKey: 'trackersBoard.widgetSettingsModal.overTime',
    descriptionKey: 'trackersBoard.widgetSettingsModal.itemsOverTimeByWeek',
    Icon: LineChart,
  },
  {
    type: 'battery',
    labelKey: 'trackersBoard.widgetSettingsModal.progress',
    descriptionKey: 'trackersBoard.widgetSettingsModal.aStatusColumnAsOne',
    Icon: BatteryMedium,
  },
  {
    type: 'text',
    labelKey: 'trackersBoard.widgetSettingsModal.heading',
    descriptionKey: 'trackersBoard.widgetSettingsModal.aTextBlockToLabel',
    Icon: TypeIcon,
  },
];

interface WidgetSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: CustomColumn[];
  items: DashboardItem[];
  /** The widget being edited; omit/null to add a new one. */
  initial?: DashboardWidget | null;
  /** Add mode → append the new widget; edit mode → replace by id. */
  onSubmit: (widget: DashboardWidget) => void;
}

/** Columns a widget type can source from. */
function eligibleColumns(type: DashboardWidgetType, columns: CustomColumn[]): CustomColumn[] {
  switch (type) {
    case 'kpi':
      return columns.filter((c) => c.type === 'number');
    case 'donut':
    case 'bar':
      return columns.filter((c) => CATEGORICAL_TYPES.has(c.type));
    case 'battery':
      return columns.filter((c) => c.type === 'status');
    case 'line':
      return columns.filter((c) => DATE_TYPES.has(c.type));
    default:
      return [];
  }
}

function autoTitle(type: DashboardWidgetType, column: CustomColumn | null, agg: string): string {
  switch (type) {
    case 'kpi':
      if (!column || agg === 'count')
        return i18n.t('trackers:trackersBoard.widgetSettingsModal.totalItems');
      return agg === 'sum'
        ? i18n.t('trackers:trackersBoard.widgetSettingsModal.totalColumn', {
            column: column.label.toLowerCase(),
          })
        : i18n.t('trackers:trackersBoard.widgetSettingsModal.averageColumn', {
            column: column.label.toLowerCase(),
          });
    case 'donut':
    case 'bar':
      return column
        ? i18n.t('trackers:trackersBoard.widgetSettingsModal.itemsByColumn', {
            column: column.label.toLowerCase(),
          })
        : i18n.t('trackers:trackersBoard.widgetSettingsModal.itemsByNoColumn');
    case 'battery':
      return column
        ? i18n.t('trackers:trackersBoard.widgetSettingsModal.columnProgress', {
            column: column.label,
          })
        : i18n.t('trackers:trackersBoard.widgetSettingsModal.progress');
    case 'line':
      return i18n.t('trackers:trackersBoard.widgetSettingsModal.itemsOverTime');
    case 'text':
      return i18n.t('trackers:trackersBoard.widgetSettingsModal.heading');
    default:
      return i18n.t('trackers:trackersBoard.widgetSettingsModal.widget');
  }
}

const DEFAULT_SIZE: Record<DashboardWidgetType, 'sm' | 'md' | 'lg'> = {
  kpi: 'sm',
  donut: 'md',
  bar: 'md',
  line: 'lg',
  battery: 'md',
  text: 'lg',
};

export function WidgetSettingsModal({
  open,
  onOpenChange,
  columns,
  items,
  initial,
  onSubmit,
}: WidgetSettingsModalProps) {
  const { t } = useTranslation('trackers');

  const editing = Boolean(initial);

  const [type, setType] = useState<DashboardWidgetType>('kpi');
  const [columnChoice, setColumnChoice] = useState<string | null>(null);
  const [agg, setAgg] = useState<DashboardAggregation>('count');
  const [bucket, setBucket] = useState<DashboardDateBucket>('week');
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);

  // Seed the form each time the modal opens: from `initial` (edit) or blank
  // defaults (add). Single source of truth, so there's no stale state to reset.
  useEffect(() => {
    if (!open) return;
    if (initial) {
      setType(initial.type);
      setColumnChoice(
        initial.config?.columnId ??
          (initial.type === 'kpi' ? ALL_ITEMS : initial.type === 'line' ? CREATED_AT : null),
      );
      setAgg(initial.config?.agg ?? 'count');
      setBucket(initial.config?.dateBucket ?? 'week');
      setText(initial.config?.text ?? '');
      setTitle(initial.title);
      setTitleTouched(true);
    } else {
      setType('kpi');
      setColumnChoice(null);
      setAgg('count');
      setBucket('week');
      setText('');
      setTitle('');
      setTitleTouched(false);
    }
  }, [open, initial]);

  const eligible = useMemo(() => eligibleColumns(type, columns), [type, columns]);

  // Effective column: the explicit choice, else the first eligible one for
  // types that require a source (donut/bar/battery).
  const requiresColumn = type === 'donut' || type === 'bar' || type === 'battery';
  const resolvedChoice: string | undefined =
    columnChoice ?? (requiresColumn ? eligible[0]?.id : type === 'kpi' ? ALL_ITEMS : CREATED_AT);
  const column =
    resolvedChoice && resolvedChoice !== ALL_ITEMS && resolvedChoice !== CREATED_AT
      ? (columns.find((c) => c.id === resolvedChoice) ?? null)
      : null;

  const effectiveAgg: DashboardAggregation = type !== 'kpi' ? 'count' : column ? agg : 'count';
  const previewTitle =
    titleTouched && title.trim() ? title.trim() : autoTitle(type, column, effectiveAgg);

  const previewWidget: DashboardWidget = useMemo(
    () => ({
      id: '__preview__',
      type,
      title: previewTitle,
      size: DEFAULT_SIZE[type],
      config: {
        ...(column ? { columnId: column.id } : {}),
        ...(type === 'kpi' ? { agg: effectiveAgg } : {}),
        ...(type === 'line' ? { dateBucket: bucket } : {}),
        ...(type === 'text'
          ? { text: text || t('trackersBoard.widgetSettingsModal.sectionHeading') }
          : {}),
      },
    }),
    [type, previewTitle, column, effectiveAgg, bucket, text, t],
  );

  const canSubmit = type === 'text' ? text.trim().length > 0 : !requiresColumn || column !== null;

  const handleSubmit = () => {
    // Edit: keep the widget's id + grid geometry, swap type/title/config.
    const widget: DashboardWidget = initial
      ? { ...previewWidget, id: initial.id, size: initial.size, w: initial.w, h: initial.h }
      : { ...previewWidget, id: uuidv4() };
    onSubmit(widget);
    onOpenChange(false);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        editing
          ? t('trackersBoard.widgetSettingsModal.editWidget')
          : t('trackersBoard.widgetSettingsModal.addWidget')
      }
      className="w-[min(calc(100vw-2rem),56rem)] sm:max-w-4xl"
    >
      <FormDialog.Body>
        <div className="flex h-[26rem] gap-4">
          <div className="grid w-64 shrink-0 auto-rows-min content-start gap-2 overflow-y-auto border-r border-border pr-4">
            {WIDGET_TYPES.map(({ type: widgetType, labelKey, descriptionKey, Icon }) => (
              <button
                key={widgetType}
                type="button"
                onClick={() => {
                  setType(widgetType);
                  setColumnChoice(null);
                  setTitleTouched(false);
                }}
                aria-pressed={widgetType === type}
                className={`flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  widgetType === type
                    ? 'border-primary bg-accent'
                    : 'border-border bg-background hover:border-primary hover:bg-accent'
                }`}
              >
                <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-foreground">{t(labelKey)}</span>
                  <span className="block text-xs text-muted-foreground">{t(descriptionKey)}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <FieldLabel className="col-span-2 flex w-full flex-col items-start gap-1 text-xs font-medium text-muted-foreground">
                {t('trackersBoard.widgetSettingsModal.title')}
                <Input
                  value={titleTouched ? title : previewTitle}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setTitleTouched(true);
                  }}
                  placeholder={previewTitle}
                />
              </FieldLabel>

              {type === 'text' ? (
                <FieldLabel className="col-span-2 flex w-full flex-col items-start gap-1 text-xs font-medium text-muted-foreground">
                  {t('trackersBoard.widgetSettingsModal.text')}
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t('trackersBoard.widgetSettingsModal.eGDeliveryHealth')}
                  />
                </FieldLabel>
              ) : (
                <FieldLabel className="flex w-full flex-col items-start gap-1 text-xs font-medium text-muted-foreground">
                  {t('trackersBoard.widgetSettingsModal.source')}
                  <Select value={resolvedChoice} onValueChange={setColumnChoice}>
                    <SelectTrigger className="h-9">
                      <SelectValue
                        placeholder={t('trackersBoard.widgetSettingsModal.pickAColumn')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {type === 'kpi' ? (
                        <SelectItem value={ALL_ITEMS}>
                          {t('trackersBoard.widgetSettingsModal.allItems')}
                        </SelectItem>
                      ) : null}
                      {type === 'line' ? (
                        <SelectItem value={CREATED_AT}>
                          {t('trackersBoard.widgetSettingsModal.createdDate')}
                        </SelectItem>
                      ) : null}
                      {eligible.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldLabel>
              )}

              {type === 'kpi' && column ? (
                <FieldLabel className="flex w-full flex-col items-start gap-1 text-xs font-medium text-muted-foreground">
                  {t('trackersBoard.widgetSettingsModal.calculation')}
                  <Select value={agg} onValueChange={(v) => setAgg(v as DashboardAggregation)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* eslint-disable i18next/no-literal-string -- aggregation wire enum values */}
                      <SelectItem value="sum">
                        {t('trackersBoard.widgetSettingsModal.sum')}
                      </SelectItem>
                      <SelectItem value="avg">
                        {t('trackersBoard.widgetSettingsModal.average')}
                      </SelectItem>
                      <SelectItem value="count">
                        {t('trackersBoard.widgetSettingsModal.count')}
                      </SelectItem>
                      {/* eslint-enable i18next/no-literal-string */}
                    </SelectContent>
                  </Select>
                </FieldLabel>
              ) : null}

              {type === 'line' ? (
                <FieldLabel className="flex w-full flex-col items-start gap-1 text-xs font-medium text-muted-foreground">
                  {t('trackersBoard.widgetSettingsModal.groupBy')}
                  <Select value={bucket} onValueChange={(v) => setBucket(v as DashboardDateBucket)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* eslint-disable i18next/no-literal-string -- date bucket wire enum values */}
                      <SelectItem value="week">
                        {t('trackersBoard.widgetSettingsModal.week')}
                      </SelectItem>
                      <SelectItem value="month">
                        {t('trackersBoard.widgetSettingsModal.month')}
                      </SelectItem>
                      {/* eslint-enable i18next/no-literal-string */}
                    </SelectContent>
                  </Select>
                </FieldLabel>
              ) : null}
            </div>

            {/* Live preview from the board's real rows */}
            <div className="flex min-h-0 flex-1 flex-col gap-2 rounded-xl border border-dashed border-border bg-background p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('trackersBoard.widgetSettingsModal.preview')}
              </div>
              <div className="text-sm font-medium text-foreground">{previewTitle}</div>
              <div className="min-h-0 flex-1 overflow-hidden">
                <DashboardWidgetBody widget={previewWidget} items={items} column={column} />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('trackersBoard.widgetSettingsModal.cancel')}
              </Button>
              <Button type="button" disabled={!canSubmit} onClick={handleSubmit}>
                {editing
                  ? t('trackersBoard.widgetSettingsModal.saveChanges')
                  : t('trackersBoard.widgetSettingsModal.addWidget')}
              </Button>
            </div>
          </div>
        </div>
      </FormDialog.Body>
    </FormDialog>
  );
}
